import { Icon, IconSize, IconVariants } from "@next-platform/client-common-spicy-ui";
import {
  AnalyticsEvents,
  CHARACTER_MODERATION_STATUS,
  CHARACTER_VISIBILITY,
  useUserInfo,
  useUserSettings,
} from "@next-platform/client-common-core";
import {
  Card,
  CardBody,
  CardFooter,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownSection,
  DropdownTrigger,
  Image,
  Tooltip,
} from "@nextui-org/react";
import PropTypes from "prop-types";
import { Fragment, useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";

import { BlockCreatorModal } from "@/components";
import CharacterRatingScore from "@/components/Character/CharacterRatingScore";
import MessageCounter from "@/components/Character/MessageCounter";
import ConfirmationModal from "@/components/common/ConfirmationModal";
import HeartIcon from "@/components/common/HeartIcon";
import ModerationWarningModal from "@/components/common/ModerationWarningModal";
import TextStyles from "@/components/common/TextStyles";
import { Col, Row, SpicyButton, Text } from "@/components/customComponents";
import Grid from "@/components/customComponents/Grid";
import { LocalizedLink } from "@/components/LocalizedLink/LocalizedLink";
import Trans from "@/components/Trans/Trans";
import { EXTERNAL_PERMISSIONS, ROUTES, SPICYCHAT_DISCORD } from "@/constants";
import { useRecombee } from "@/hooks";
import useLocalizedLocation from "@/hooks/useLocalizedLocation";
import { useLocalSettings } from "@/hooks/useLocalSettings.ts";
import useQueueLogEvent from "@/hooks/useQueueLogEvent";
import useAuth from "@/services/useAuth";
import { AvatarPreset } from "@/types";
import { cn, convertToSnakeCase, getAvatarUrl, getBusinessRouteScreen, getProfilePathForCharacter } from "@/utils";

const restrictedModalContactUsLinkText = "Please contact us.";
const restrictedModalTitle = "Restricted!";
const restrictedModalConfirmationMessage = `Your account currently has restricted access. You can continue to chat, but uploading and editing bots is temporarily disabled. For more information, `;

const CHARACTER_NAME_LONG_THRESHOLD = 20;
const CHARACTER_TITLE_TRUNCATE_LENGTH = 100;
const CHARACTER_TITLE_LONG_THRESHOLD = 80;

const renderCharacterCardModerationLabel = (moderationStatus) => {
  switch (moderationStatus) {
    case CHARACTER_MODERATION_STATUS.FIX_REQUIRED:
      return <Trans i18nKey="chatbot:moderation.actionRequired" />;

    case CHARACTER_MODERATION_STATUS.PENDING:
    case CHARACTER_MODERATION_STATUS.REREVIEW_REQUIRED:
      return <Trans i18nKey="chatbot:moderation.underReview" />;

    case CHARACTER_MODERATION_STATUS.MANUAL_REVIEW_REQUIRED:
      return <Trans i18nKey="chatbot:moderation.underManualReview" />;

    default:
      return null;
  }
};

const CharacterCard = ({
  character,
  onDeleteCharacter,
  isLoading,
  hideOptions = false,
  setErrorModalVisible,
  setErrorModalMessage,
  draft,
  sortBy,
  // eslint-disable-next-line complexity -- CharacterCard is a legacy component with many conditional rendering paths; full refactor is tracked separately
}) => {
  const { logEvent } = useQueueLogEvent();
  const { pagePath } = useLocalizedLocation();
  const screenName = getBusinessRouteScreen(pagePath);
  const { sendUserInteractionDetailView, recombeeUserId } = useRecombee();
  const { isPartner } = useLocalSettings();
  const [showDeleteConfirmationDialog, setShowDeleteConfirmationDialog] = useState(false);
  const [moderationModalVisible, setModerationModalVisible] = useState(false);
  const [showBanDialog, setShowBanDialog] = useState(false);
  const handler = () => setModerationModalVisible(true);
  const { permissions, onlyUserId: user_id } = useUserInfo();
  const { blockedUsers, blurNSFW, likedBots } = useUserSettings();
  const isLiked = likedBots?.includes(character?.id);
  const isCharacterCreatorBlocked = blockedUsers?.includes(character?.creator_user_id);
  const isAvatarImageBlurred = blurNSFW && character?.avatar_is_nsfw;
  const [showBlockModal, setShowBlockModal] = useState(false);
  const navigate = useNavigate();

  const { isAuthenticated } = useAuth();
  const [isImageLoaded, setIsImageLoaded] = useState(false);
  const handleImageLoad = useCallback(() => setIsImageLoaded(true), []);

  const onDropdownAction = useCallback(
    (key) => {
      if (key === "delete") {
        const hasPermission = permissions?.includes(EXTERNAL_PERMISSIONS.delete_character);
        if (!hasPermission) return setShowBanDialog(true);
        setShowDeleteConfirmationDialog(true);
      }
      if (key === "blockCreator") {
        setShowBlockModal(true);
      }
    },
    [permissions, setShowBanDialog, setShowDeleteConfirmationDialog, setShowBlockModal],
  );

  const handleClose = useCallback(() => {
    setShowDeleteConfirmationDialog(false);
    setShowBanDialog(false);
    setShowBlockModal(false);
  }, [setShowDeleteConfirmationDialog, setShowBanDialog, setShowBlockModal]);

  const isCharacterCreator = user_id && user_id === character.creator_user_id;
  let moderationStatus = character.aicq_manual_review_required
    ? CHARACTER_MODERATION_STATUS.MANUAL_REVIEW_REQUIRED
    : character?.moderationReport?.moderation_status;

  if (!Object.values(CHARACTER_MODERATION_STATUS).includes(moderationStatus)) {
    moderationStatus = null;
  }

  const isFixRequired = moderationStatus === CHARACTER_MODERATION_STATUS.FIX_REQUIRED;

  const { moderationBorder, moderationColor } = TextStyles.moderationStyles(moderationStatus);
  const moderationLabel = renderCharacterCardModerationLabel(moderationStatus);
  const shouldRenderModerationBadge = moderationStatus && moderationStatus !== CHARACTER_MODERATION_STATUS.CLOSED;

  const handleBotSelectEvent = useCallback(() => {
    const botCategory = convertToSnakeCase(sortBy);

    logEvent(AnalyticsEvents.CHATBOT_SELECT, {
      bot_category: botCategory,
      character_id: character?.id || character?.character_id,
      page_name: screenName,
    });
  }, [logEvent, sortBy, character, screenName]);

  const handleProfileNavigation = useCallback(() => {
    return getProfilePathForCharacter(character);
  }, [character]);

  const handleCardClick = useCallback(
    (e) => {
      e.preventDefault();
      if (character.aicq_manual_review_required) return;
      const targetUrl = e.currentTarget.getAttribute("href");
      if (draft) navigate(targetUrl);
      else if (!moderationStatus || moderationStatus === CHARACTER_MODERATION_STATUS.CLOSED) {
        // if character is able to chat & it's visibility is public then only send recombee event
        const charId = character?.id || character?.character_id;
        if (character?.visibility === CHARACTER_VISIBILITY.PUBLIC) {
          void sendUserInteractionDetailView(recombeeUserId, charId, character?.recommId ? { recommId: character.recommId } : undefined);
        }
        handleBotSelectEvent();
        navigate(targetUrl);
      } else {
        handler();
      }
    },
    [character, draft, moderationStatus, sendUserInteractionDetailView, recombeeUserId, handleBotSelectEvent, navigate],
  );

  const handleDeleteCharacter = useCallback(() => onDeleteCharacter(character.id), [onDeleteCharacter, character.id]);

  function getCardURL() {
    if (draft) return `/chatbot/${isFixRequired ? "edit" : "create"}/${character.id}`;
    if (!moderationStatus || moderationStatus === CHARACTER_MODERATION_STATUS.CLOSED || moderationStatus === CHARACTER_MODERATION_STATUS.PENDING) {
      return `/chat/${character.id}`;
    }
  }

  return (
    <>
      <Card
        variant="bordered"
        className={
          "bg-cardColor relative z-[0] max-w-[200px] w-full h-[270px] align-middle justify-between shadow-none border-1 border-solid border-border " +
          moderationBorder
        }
      >
        {shouldRenderModerationBadge ? (
          <Grid className="absolute z-[1]">
            <SpicyButton
              className={
                "rounded-none rounded-tl-lg rounded-br-lg" +
                (character.aicq_manual_review_required ? " [&_span]:text-foreground" : " [&_span]:text-black")
              }
              variant="secondary_short"
              color={moderationColor}
            >
              <Text b type="black">
                {moderationLabel}
              </Text>
            </SpicyButton>
          </Grid>
        ) : null}

        {!draft && (
          <div className="absolute top-[5px] right-[5px] cursor-pointer z-[1] ">
            <Tooltip content={character?.definition_visible ? "Chatbot Profile, definition visible" : "Chatbot Profile"}>
              <LocalizedLink to={handleProfileNavigation()} className="text-current" aria-label="chatbot-profile">
                {character?.definition_visible ? (
                  <svg width="0" height="0" className="absolute">
                    <linearGradient id="gradient" x1="100%" y1="100%" x2="75%" y2="40%">
                      <stop stopColor="#066ff3" offset="0%" />
                      <stop stopColor="#8945de" offset="100%" />
                    </linearGradient>
                  </svg>
                ) : null}
                <Icon
                  icon={IconVariants.INFO}
                  size={IconSize.SMALL}
                  className={"opacity-[0.6] " + (character?.definition_visible && "fill-[url(#gradient)] !opacity-[1]")}
                />
              </LocalizedLink>
            </Tooltip>
          </div>
        )}

        {user_id && !isCharacterCreator && !isCharacterCreatorBlocked ? (
          <div className="absolute top-[5px] left-[5px] cursor-pointer opacity-[1] z-10">
            <HeartIcon
              character={character}
              setErrorModalVisible={setErrorModalVisible}
              setErrorModalMessage={setErrorModalMessage}
              isLiked={isLiked}
            />
          </div>
        ) : null}

        <CardBody className="flex items-center min-h-[235px] overflow-hidden py-5 px-1.5">
          {(character.visibility === CHARACTER_VISIBILITY.PRIVATE || character.visibility === CHARACTER_VISIBILITY.PERMANENTLY_PRIVATE) && (
            <Icon icon={IconVariants.LOCK} size={IconSize.SMALL} className="absolute top-[5px] left-[5px] text-link" />
          )}
          {character.visibility === CHARACTER_VISIBILITY.HIDDEN && (
            <Icon icon={IconVariants.UNLOCK} size={IconSize.SMALL} className="absolute top-[5px] left-[8px] text-link" />
          )}
          {Object.prototype.hasOwnProperty.call(character, "recommId") && (
            <Grid className="absolute top-[10px] left-[50%] -translate-x-[50%] z-[1]">
              <SpicyButton variant="secondary_short" color="secondary_strong">
                Recommended
              </SpicyButton>
            </Grid>
          )}
          <LocalizedLink
            className={cn("no-underline w-full h-full", character.aicq_manual_review_required ? "cursor-not-allowed" : "cursor-pointer")}
            to={getCardURL()}
            onClick={handleCardClick}
            aria-label={draft ? `edit-${character.name}` : `chat-with-${character.name}`}
          >
            {character?.avatar_url ? (
              <div className={`w-max mx-auto ${isAvatarImageBlurred ? "blur" : "blur-none"}`}>
                <Image
                  removeWrapper
                  loading="lazy"
                  className={cn(
                    "object-contain rounded-[10px] z-0 max-w-full",
                    isImageLoaded ? "!opacity-100" : null,
                    character.aicq_manual_review_required ? "grayscale" : null,
                  )}
                  src={getAvatarUrl(character?.avatar_url, {
                    preset: AvatarPreset.AVATAR_128X128,
                  })}
                  alt={character.name}
                  onLoad={handleImageLoad}
                  width={128}
                  height={128}
                />
              </div>
            ) : (
              <div className="size-32 bg-[#D9D9D9] rounded-[10px] flex items-center justify-center mx-auto">
                <Icon icon={IconVariants.IMAGE_OFF} size={IconSize.XLARGE} className="text-[#000000] text-[28px]" />
              </div>
            )}
          </LocalizedLink>

          <Grid
            justify="center"
            className={
              "[&>*:first-child]:overflow-hidden [&>*:first-child]:whitespace-nowrap [&>*:first-child]:text-ellipsis [&>*:first-child]:font-semibold"
            }
          >
            <Text variant={character?.name?.length >= CHARACTER_NAME_LONG_THRESHOLD ? "sm" : "md"}>{character.name}</Text>
          </Grid>
          <Grid justify="center">
            <SpicyButton as={LocalizedLink} variant="primary_small_short" color="transparent" to={`/creator/${character.creator_username}`}>
              <div
                className={
                  "h-5 flex items-center decoration-transparent hover:decoration-secondary" + (character.aicq_manual_review_required && " grayscale")
                }
              >
                <Text variant="xs" type="secondary normal">
                  @{character.creator_username}
                </Text>
              </div>
            </SpicyButton>
          </Grid>
          <Text variant={character.title?.length > CHARACTER_TITLE_LONG_THRESHOLD ? "xs" : "sm"} type={"characterTitle"}>
            {character.title?.length > CHARACTER_TITLE_TRUNCATE_LENGTH
              ? `${character.title.slice(0, CHARACTER_TITLE_TRUNCATE_LENGTH)}  ...`
              : character.title}
          </Text>
        </CardBody>

        <CardFooter className="pr-0 border-0 border-t-1 border-solid border-[rgba(121,104,104,0.30)] px-0">
          <Row className="ml-[5px] w-full pr-1" align="center">
            <Col>
              <MessageCounter num_messages={character.num_messages} />
            </Col>
            <Col>
              <Row justify="end" align="center">
                <CharacterRatingScore rating_score={character?.rating_score} />
                {isCharacterCreatorBlocked && !isCharacterCreator && isPartner ? (
                  <Fragment />
                ) : (
                  !isCharacterCreatorBlocked &&
                  !hideOptions && (
                    <Dropdown offset={-5} classNames={TextStyles.dropdown.base} className="card-dropdown list-none">
                      <DropdownTrigger>
                        <SpicyButton isIconOnly variant="primary_smallest">
                          <Icon icon={IconVariants.THREE_DOTS} size={IconSize.SMALL} />
                        </SpicyButton>
                      </DropdownTrigger>

                      <DropdownMenu classNames={TextStyles.dropdown.menu} variant="flat" aria-label="dropdown Actions" onAction={onDropdownAction}>
                        <DropdownSection classNames={TextStyles.dropdown.section} showDivider></DropdownSection>

                        {isCharacterCreator && !draft ? (
                          <DropdownSection classNames={TextStyles.dropdown.section} showDivider>
                            <DropdownItem classNames={TextStyles.dropdown.item_primary} key="edit">
                              <LocalizedLink to={`/chatbot/edit/${character.id}`} aria-label={`edit-${character.name}`}>
                                <Text variant={"md"}>
                                  <Trans i18nKey="chatbot:editChatbot.title" />
                                </Text>
                              </LocalizedLink>
                            </DropdownItem>
                          </DropdownSection>
                        ) : null}

                        {isCharacterCreator ? (
                          <DropdownSection classNames={TextStyles.dropdown.section} showDivider={!draft}>
                            <DropdownItem classNames={TextStyles.dropdown.item_danger} key="delete" color="danger">
                              <Text variant={"md"} type={"error"}>
                                Delete Chatbot
                              </Text>
                            </DropdownItem>
                          </DropdownSection>
                        ) : null}

                        {!isPartner && !draft && (
                          <DropdownSection classNames={TextStyles.dropdown.section}>
                            <DropdownItem classNames={TextStyles.dropdown.item_primary} key="report" color="transparent">
                              <LocalizedLink to={`${ROUTES.report}/${character.id}`} aria-label={`report-${character.name}`}>
                                <Text variant={"md"}>Report Chatbot</Text>
                              </LocalizedLink>
                            </DropdownItem>
                          </DropdownSection>
                        )}

                        {!isCharacterCreator && isAuthenticated && character?.creator_user_id ? (
                          <DropdownSection classNames={TextStyles.dropdown.section}>
                            <DropdownItem classNames={TextStyles.dropdown.item_danger} key="blockCreator" color="danger">
                              <Text variant={"md"} type={"error"}>
                                Block Creator
                              </Text>
                            </DropdownItem>
                          </DropdownSection>
                        ) : null}
                      </DropdownMenu>
                    </Dropdown>
                  )
                )}
              </Row>
            </Col>
          </Row>
        </CardFooter>
      </Card>

      {showBlockModal ? (
        <BlockCreatorModal
          creatorId={character.creator_user_id}
          creatorUsername={character.creator_username}
          isVisible={showBlockModal}
          onClose={setShowBlockModal}
        />
      ) : null}
      <ModerationWarningModal
        moderationModalVisible={moderationModalVisible}
        setModerationModalVisible={setModerationModalVisible}
        character={character}
      />
      <ConfirmationModal
        heading="Delete Chatbot"
        handleClose={handleClose}
        isLoading={isLoading}
        onUserConfirmation={handleDeleteCharacter}
        confirmationMessage={"Chatbot will be deleted. Do you want to continue?"}
        isVisible={showDeleteConfirmationDialog}
        onChat={handleBotSelectEvent}
      />
      <ConfirmationModal
        handleClose={handleClose}
        heading={restrictedModalTitle}
        confirmationMessage={restrictedModalConfirmationMessage}
        link={SPICYCHAT_DISCORD}
        linkText={restrictedModalContactUsLinkText}
        isVisible={showBanDialog}
        showButtons={false}
      />
    </>
  );
};

CharacterCard.propTypes = {
  character: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    title: PropTypes.string,
    description: PropTypes.string,
    avatar_url: PropTypes.string,
    avatar_is_nsfw: PropTypes.bool,
    creator_user_id: PropTypes.string,
    creator_username: PropTypes.string,
    num_messages: PropTypes.number,
    visibility: PropTypes.string,
    is_underage: PropTypes.bool,
    definition_visible: PropTypes.bool,
    lora_status: PropTypes.string,
    recommId: PropTypes.string,
    moderationReport: PropTypes.string,
    rating_score: PropTypes.number,
  }),
  draft: PropTypes.bool,
  onPress: PropTypes.func,
  onDeleteCharacter: PropTypes.func,
  hideOptions: PropTypes.bool,
  setErrorModalVisible: PropTypes.func,
  setErrorModalMessage: PropTypes.func,
  isLoading: PropTypes.bool,
};

export default CharacterCard;
